import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import LocationPermission from "../../components/common/LocationPermission";
import OptionSelect from "../../components/common/OptionSelect";
import {
  loadInitialData,
  loadMunicipalities,
  loadWards,
  loadCanteens,
} from "../../features/canteen/canteenActions";

import {
  setSelectedDistrict,
  setSelectedMunicipality,
  setSelectedWard,
  setSelectedCanteenType,
  setError,
  resetMunicipalityData,
  resetWardData,
} from "../../features/canteen/canteenSlices";

import { selectCanteenState } from "../../features/canteen/canteenSelectors";
import { dismissLocationPrompt, checkLocationPermission } from "../../features/location/locationSlice";
import { selectShouldShowLocationPrompt } from "../../features/location/locationSelectors";
import { getSurveyTypeSlug } from "../../utils/forms";

import { useTranslation } from "../../hooks/useTranslations";

function HomePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hasSearched, setHasSearched] = useState(false);
  const shouldShowLocationPrompt = useSelector(selectShouldShowLocationPrompt);

  const {
    districts,
    municipalities,
    wards,
    canteenTypes,
    canteens,
    selectedDistrict,
    selectedMunicipality,
    selectedWard,
    selectedCanteenType,
    loading,
    error,
  } = useSelector(selectCanteenState);

  useEffect(() => {
    dispatch(loadInitialData());
    dispatch(checkLocationPermission());
  }, [dispatch]);

  const {t} = useTranslation();

  const handleDistrictChange = (districtId) => {
    dispatch(setSelectedDistrict(districtId));
    dispatch(resetMunicipalityData());
    if (!districtId) return;
    dispatch(loadMunicipalities(districtId));
  };

  const handleMunicipalityChange = (municipalityId) => {
    dispatch(setSelectedMunicipality(municipalityId));
    dispatch(resetWardData());
    if (!municipalityId) return;
    dispatch(loadWards(municipalityId));
  };

  const handleSubmit = () => {
    if (!selectedDistrict) return dispatch(setError("Please select a district"));
    if (!selectedMunicipality)
      return dispatch(setError("Please select a municipality"));
    if (!selectedWard) return dispatch(setError("Please select a ward"));
    if (!selectedCanteenType)
      return dispatch(setError("Please select a canteen type"));

    setHasSearched(true);

    const payload = {
      district_id: Number(selectedDistrict),
      municipality_id: Number(selectedMunicipality),
      ward_id: Number(selectedWard),
      canteen_type: Number(selectedCanteenType),
    };

    dispatch(loadCanteens(payload));
  };

  const getSelectedCanteenTypeName = () =>
    canteenTypes.find((type) => String(type.id) === String(selectedCanteenType))
      ?.type_name || "";


  const handleStartSurvey = (canteen) => {
    const surveyType = getSurveyTypeSlug(
      canteen.canteen_type ||
      canteen.canteen_type_name ||
      canteen.type_name ||
      getSelectedCanteenTypeName()
    );

    if (!surveyType) {
      dispatch(setError("Unable to identify the survey type for this canteen"));
      return;
    }

    navigate(`/survey/${surveyType}`, {
      state: {
        canteen,
        canteenType: getSelectedCanteenTypeName(),
        // Pass selection IDs so SurveyPage can include them in the API payload
        districtId: selectedDistrict,
        municipalityId: selectedMunicipality,
        wardId: selectedWard,
      },
    });
  };

  return (
    <div className="min-h-screen bg-background text-text font-sans pb-12">
      {shouldShowLocationPrompt && (
        <LocationPermission
          variant="modal"
          onClose={() => dispatch(dismissLocationPrompt())}
        />
      )}

      {/* Header */}
      {/* <Navbar /> */}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* Search & Filter Section */}
        <section className="bg-surface rounded-2xl shadow-sm border border-border p-5 sm:p-6">
          <div className="mb-5 border-b border-border pb-4">
            <h2 className="text-xl font-bold text-text">{t("home.findCanteen")}</h2>
            <p className="text-sm text-text-muted mt-1">{t("home.subtitle")}</p>
          </div>

          {error && (
            <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 flex items-start gap-3">
              <svg className="w-5 h-5 text-error mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-error">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <OptionSelect
              id="district"
              label={t("home.district")}
              value={selectedDistrict}
              onChange={handleDistrictChange}
              options={districts}
              placeholder="Select District"
              getOptionLabel={(district) => district.name}
              getOptionValue={(district) => district.id}
            />

            <OptionSelect
              id="municipality"
              label={t("home.municipality")}
              value={selectedMunicipality}
              onChange={handleMunicipalityChange}
              options={municipalities}
              placeholder="Select Municipality"
              disabled={!selectedDistrict}
              getOptionLabel={(municipality) => municipality.name}
              getOptionValue={(municipality) => municipality.id}
            />

            <OptionSelect
              id="ward"
              label={t("home.ward")}
              value={selectedWard}
              onChange={(wardId) => dispatch(setSelectedWard(wardId))}
              options={wards}
              placeholder="Select Ward"
              disabled={!selectedMunicipality}
              getOptionLabel={(ward) => ward.name}
              getOptionValue={(ward) => ward.id}
            />

            <OptionSelect
              id="canteen-type"
              label={t("home.canteenType")}
              value={selectedCanteenType}
              onChange={(canteenTypeId) =>
                dispatch(setSelectedCanteenType(canteenTypeId))
              }
              options={canteenTypes}
              placeholder="Select Type"
              getOptionLabel={(type) => type.type_name}
              getOptionValue={(type) => type.id}
            />
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary hover:bg-orange-600 text-white font-semibold py-3 px-8 rounded-xl transition-all duration-200 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t("home.searching")}
                </>
              ) : (
                t("home.searchCanteens")
              )}
            </button>
          </div>
        </section>

        {/* Results Section */}
        <section>
          {hasSearched && !loading && canteens.length === 0 && (
            <div className="bg-surface rounded-3xl border border-border p-12 text-center shadow-sm">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-50 mb-5 border border-border">
                <svg className="w-10 h-10 text-text-disabled" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-text">No canteens found</h3>
              <p className="text-text-muted mt-2 max-w-sm mx-auto">Try adjusting your search criteria to find what you're looking for.</p>
            </div>
          )}

          {canteens.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-6 px-1">
                <h3 className="text-2xl font-bold text-text tracking-tight">{t("home.searchResults")}</h3>
                <span className="bg-primary/10 text-primary text-sm font-bold px-4 py-1.5 rounded-full border border-primary/20">
                  {canteens.length} Found
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {canteens.map((canteen) => (
                  <div key={canteen.id} className="bg-surface rounded-2xl border border-border shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 overflow-hidden flex flex-col h-full group">
                    <div className="p-5 flex-1 flex flex-col gap-4">
                      {/* <div className="flex items-start justify-between gap-3 border-b border-border pb-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-bold text-text group-hover:text-primary transition-colors flex-1 pt-1 leading-snug">
                          {canteen.area_name}
                        </h4>
                      </div> */}


                      {canteen.area_name && (
                        <div className="flex items-center gap-3 text-sm text-text-muted">
                          <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span><b className="font-bold text-primary">{canteen.area_name}</b></span>
                        </div>
                      )}

                      <div className="space-y-3 mt-1">
                        {canteen.kitchen_address && (
                          <div className="flex items-start gap-3 text-sm text-text-muted">
                            {/* <svg className="w-5 h-5 mt-0.5 shrink-0 text-text-disabled" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg> */}
                            <svg className="w-5 h-5 shrink-0 text-text-disabled" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                            </svg>
                            <span className="line-clamp-2 leading-relaxed">
                              <span className="text-sm font-medium text-text-muted">Address: </span>
                              {canteen.kitchen_address}
                            </span>
                          </div>
                        )}


                        {canteen.kitchen_location && (
                          <div className="flex items-center gap-3 text-sm text-text-muted">
                            <svg className="w-5 h-5 shrink-0 text-text-disabled" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="truncate">
                              <span className="text-sm font-medium text-text-muted">Location: </span>
                              {canteen.kitchen_location}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="p-4 bg-background/80 border-t border-border grid grid-cols-2 gap-3 mt-auto">
                      <button
                        onClick={() => navigate('/my-surveys', { state: { canteen } })}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-surface border border-border text-text font-semibold text-sm hover:bg-gray-50 hover:text-primary transition-all active:scale-95 shadow-sm"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        {t("home.viewSurvey")}
                      </button>
                      <button
                        onClick={() => handleStartSurvey(canteen)}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-sm transition-all active:scale-95 shadow-sm hover:shadow"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                        </svg>
                        {t("home.startSurvey")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default HomePage;
